import { ActionOrigin, AnalyticsService } from 'base/analytics';
import { LibraryCache } from 'base/library_cache';
import { SlotType } from 'base/library_service';
import { createDndDomain, DropMode } from 'base/ui/dnd';
import {
  ElementInstance,
  ElementInstanceState,
  SectionRef,
  SlotOverride,
  SlotOverrideState,
} from 'blocks/editor/model';
import { shallowCloneElementState } from 'blocks/editor/model_helpers';
import { SelectionStore } from 'blocks/editor/selection/store';
import { globalSuggestionsStore } from 'blocks/editor/suggested_edits';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { computedFn } from 'mobx-utils';
import * as React from 'react';
import { Interactable } from './interactable';
import {
  clientPointToComponentRoot,
  NodeMeasurerStore,
} from './node_measuring';
import {
  canAddToSlot,
  canReorderInSlot,
  getCanonicalElementId,
  SlotInfo,
} from './slots';
import styles from './slots_dnd.css';
import {
  computeNewLocationMarginOverrides,
  computeOldLocationMarginOverrides,
  MarginOverride,
  marginOverrideToMarginState,
} from './slots_margins';

type DragSource = {
  el: ElementInstanceState;
  from?: {
    slotInfo: SlotInfo;
    slotOverride: SlotOverride;
    slotType: SlotType;
    mutableEl: ElementInstance;
    index: number;
    parentElement: ElementInstance;
    rootElement: ElementInstance;
  };
};
type DropDest = {
  el: ElementInstance;
  slotInfo: SlotInfo;
  slotOverride: SlotOverride;
  index: number;
};
export const elementDndDomain = createDndDomain<DragSource, DropDest>({
  isSelfDrop: (source, dest) =>
    source.from?.slotOverride === dest.slotOverride &&
    (source.from.index === dest.index || source.from.index + 1 === dest.index),
  isDropAllowed(source, dest, mode) {
    // Don't allow dragging between different slots.
    if (source.from?.slotOverride !== dest.slotOverride) return false;

    const slotOverride = source.from.slotOverride;
    const slotType = source.from.slotType;

    if (mode === 'duplicate' && !canAddToSlot(slotOverride, slotType).result)
      return false;

    if (mode !== 'duplicate' && !canReorderInSlot(slotType)) return false;

    return true;
  },
  useStickyDropTargets: true,
});

export const SlotDropTargetOverlay = observer(function _SlotDropTargetOverlay({
  rootElement,
  interactables,
  measuring,
  library,
  sref,
  selection,
  analytics,
}: {
  rootElement: ElementInstance;
  interactables: readonly Interactable[];
  measuring: NodeMeasurerStore;
  library: LibraryCache;
  sref: SectionRef;
  selection: SelectionStore;
  analytics: AnalyticsService | undefined;
}) {
  const shouldRemeasureRef = React.useRef<boolean>(true);

  return (
    <elementDndDomain.DropTarget
      className={styles.dropTargetOverlay}
      opts={{
        mouseToDestination: (cx, cy, dropTargetEl, source) => {
          if (!source.from) return;

          // Don't allow dragging between different sections.
          if (source.from.rootElement !== rootElement) return;

          const hit = {
            ...source.from.slotInfo,
            element: source.from.parentElement,
          };
          const mouse = clientPointToComponentRoot(dropTargetEl, cx, cy);
          const slotOverride = hit?.element.slotOverrides.get(hit.name);

          if (!hit || !slotOverride) return;

          const interactablesByElement = new Map(
            interactables.map((i) => [i.slotItem?.itemElement, i] as const),
          );

          if (shouldRemeasureRef.current) {
            // Drag and drop is pretty dynamic and the usual render effects
            // might not be keeping positions of each item as up-to-date as we'd
            // like.
            measuring.refreshAll();
          }

          const boxes = slotOverride.items.map((item) => {
            const interactable = interactablesByElement.get(item);
            if (!interactable) {
              return;
            }

            return measuring.getRootNode(interactable.element)?.box;
          });

          const dropPreviewIndex = elementDndDomain.state.get()?.dest?.index;
          const dropPreviewBox = measuring.getRootNode('drop-preview')?.box;

          const newDestIndex = getIndexToInsertAt({
            boxes,
            dropPreviewBox,
            dropPreviewIndex,
            cursorX: mouse.x,
            cursorY: mouse.y,
            fromIndex: source.from.index,
          });

          // If these indices are different, it means the drop preview is going
          // to move on the next render (or this is the first time it's being
          // rendered). Which means next time we'll want to remeasure
          // everything.
          shouldRemeasureRef.current = newDestIndex !== dropPreviewIndex;

          return {
            el: hit.element,
            slotInfo: hit,
            slotOverride: slotOverride,
            index: newDestIndex,
          };
        },
        processDrop: (source, dest, dropMode) => {
          if (!source.from) return;
          const oldIndex = source.from.index;
          let newIndex = dest.index;

          const shouldApplyMarginOverrides = !(
            newIndex === oldIndex || newIndex === oldIndex + 1
          );

          if (shouldApplyMarginOverrides && dropMode !== 'duplicate') {
            const oldLocationMarginOverrides =
              computeOldLocationMarginOverrides({
                slotItems: dest.slotOverride.items,
                slotInfo: dest.slotInfo,
                index: oldIndex,
                library,
              });
            for (const {
              index,
              marginOverride,
            } of oldLocationMarginOverrides) {
              dest.slotOverride.items[index].margin =
                marginOverrideToMarginState(marginOverride);
            }
          }

          if (dropMode !== 'duplicate') {
            // Remove the item from its original location.
            source.from.slotOverride.items.splice(oldIndex, 1);

            if (oldIndex < newIndex) {
              newIndex--;
            }
          }

          // Add the item to its new location.
          dest.slotOverride.items.splice(
            newIndex,
            0,
            dropMode === 'duplicate'
              ? shallowCloneElementState(source.el)
              : source.el,
          );
          const newElement = dest.slotOverride.items[newIndex];

          if (shouldApplyMarginOverrides || dropMode === 'duplicate') {
            const marginOverrides = computeNewLocationMarginOverrides({
              slotItems: dest.slotOverride.items,
              slotInfo: dest.slotInfo,
              index: newIndex,
              library,
            });
            for (const { index, marginOverride } of marginOverrides) {
              dest.slotOverride.items[index].margin =
                marginOverrideToMarginState(marginOverride);
            }
          }

          const analyticsContext = {
            sectionRootSlug: rootElement.source.slug,
            slotName: dest.slotInfo.name,
            elementSlug: newElement.source.slug,
            elementRootName: newElement.source.rootName ?? '',
            elementCanonical: getCanonicalElementId(newElement, library) ?? '',
            origin: ActionOrigin.DragAndDrop,
          };

          analytics?.track(
            dropMode === 'duplicate'
              ? {
                  event: 'blocks/duplicate-element',
                  ...analyticsContext,
                }
              : {
                  event: 'blocks/move-element',
                  ...analyticsContext,
                },
          );

          selection.set(
            {
              ...selection.factory.forSection(sref),
              element: newElement,
            },
            { disableEnsureVisible: true },
          );
        },
      }}
    />
  );
});

/**
 * Returns the distance between the nearest point of the box and the point.
 **/
export function boxPointDistanceSq(
  box: { left: number; right: number; top: number; bottom: number },
  point: { x: number; y: number },
) {
  const dx = Math.max(box.left - point.x, 0, point.x - box.right);
  const dy = Math.max(box.top - point.y, 0, point.y - box.bottom);
  return dx * dx + dy * dy;
}

const getClosestBoxIndex = (
  boxes: ReadonlyArray<DOMRect | undefined>,
  { x, y }: { x: number; y: number },
) => {
  let closestBoxIndex = -1;
  let closestBoxDistance = Infinity;
  for (let i = 0; i < boxes.length; i++) {
    const box = boxes[i];
    if (!box) continue;
    const distance = boxPointDistanceSq(box, { x, y });
    if (distance < closestBoxDistance) {
      closestBoxIndex = i;
      closestBoxDistance = distance;
    }
  }
  return closestBoxIndex;
};

export const getIndexToInsertAt = ({
  boxes,
  dropPreviewBox,
  dropPreviewIndex,
  cursorX,
  cursorY,
  fromIndex,
}: {
  boxes: ReadonlyArray<DOMRect | undefined>;
  dropPreviewBox: DOMRect | undefined;
  dropPreviewIndex: number | undefined;
  cursorX: number;
  cursorY: number;
  fromIndex: number;
}): number => {
  if (boxes.length === 0) {
    return 0;
  }

  if (dropPreviewIndex === undefined) {
    // Drop preview hasn't been rendered yet, so just return the box that's
    // closest to the cursor.
    const closestBoxIndex = getClosestBoxIndex(boxes, {
      x: cursorX,
      y: cursorY,
    });
    if (fromIndex < closestBoxIndex) {
      // When we render the drop preview, it'll appear at the index we return
      // here. But, we'll also hide the original location of the item we're
      // dragging. And, if the item we're dragging comes before the closest box,
      // that hiding will cause the whole list of items to shift up by one. So,
      // we have to account for that here to make sure the drop preview appears
      // in the right spot.
      return closestBoxIndex + 1;
    }
    return closestBoxIndex;
  }

  if (dropPreviewBox === undefined) {
    // Drop preview box has been rendered, but hasn't been measured yet... just
    // stick with the same index until the box becomes available.
    return dropPreviewIndex;
  }

  const boxesWithDropPreview = [...boxes];
  boxesWithDropPreview.splice(dropPreviewIndex, 0, dropPreviewBox);

  // Find the closest box to the cursor, including the last drop preview box
  // we've rendered.
  const closestBoxIndex = getClosestBoxIndex(boxesWithDropPreview, {
    x: cursorX,
    y: cursorY,
  });

  if (closestBoxIndex === dropPreviewIndex) {
    // The closest box to the cursor is the last drop preview box we rendered.
    return dropPreviewIndex;
  }

  // The closest box to the cursor is something other than the last drop
  // preview box we rendered.

  // Only move the drop preview to this new index if certain conditions are
  // met. If we didn't check these, we run the risk of drag and drop getting
  // into an unstable state where we rapidly switch between two indices.

  const closestBox = boxesWithDropPreview[closestBoxIndex]!;

  const canMoveDown = cursorY > closestBox.bottom - dropPreviewBox.height;
  const canMoveUp = cursorY < closestBox.top + dropPreviewBox.height;

  const canMoveLeft = cursorX < closestBox.left + dropPreviewBox.width;
  const canMoveRight = cursorX > closestBox.right - dropPreviewBox.width;

  const dx = closestBox.left - dropPreviewBox.left;
  const dy = closestBox.top - dropPreviewBox.top;

  const horizontalDirection = dx > 1 ? 'right' : dx < -1 ? 'left' : null;
  const verticalDirection = dy > 1 ? 'down' : dy < -1 ? 'up' : null;

  const canMove = () => {
    if (horizontalDirection === null) {
      if (verticalDirection === 'down') return canMoveDown;
      if (verticalDirection === 'up') return canMoveUp;
      return false;
    }
    if (verticalDirection === null) {
      if (horizontalDirection === 'right') return canMoveRight;
      if (horizontalDirection === 'left') return canMoveLeft;
      return false;
    }
    if (horizontalDirection === 'left' && verticalDirection === 'up')
      return canMoveUp && canMoveLeft;
    if (horizontalDirection === 'right' && verticalDirection === 'up')
      return canMoveUp && canMoveRight;
    if (horizontalDirection === 'left' && verticalDirection === 'down')
      return canMoveDown && canMoveLeft;
    if (horizontalDirection === 'right' && verticalDirection === 'down')
      return canMoveDown && canMoveRight;
    return false;
  };

  return canMove() ? closestBoxIndex : dropPreviewIndex;
};

type RenderableSlotItem = {
  item: ElementInstance | ElementInstanceState;
  marginOverride?: MarginOverride;
} & (
  | { type?: undefined }
  | { type: 'addSuggestion' }
  | { type: 'replacementSuggestion'; replacement: ElementInstanceState }
  | { type: 'dropPreview' }
);

const getItemsWithSuggestions = computedFn(function (
  slotOverride: SlotOverride | SlotOverrideState,
  slotInfo: SlotInfo,
  library: LibraryCache,
): ReadonlyArray<RenderableSlotItem> {
  // Tack a null item onto the end so we check for add suggestions at the end of the slot.
  const items: RenderableSlotItem[] = [...slotOverride.items, null].flatMap(
    (item, i) => {
      if (item) {
        const replacementSuggestion = item?.mutable
          ? globalSuggestionsStore.getSuggestedElementReplacement(item)
          : undefined;

        if (replacementSuggestion) {
          return [
            {
              item,
              type: 'replacementSuggestion',
              replacement: replacementSuggestion,
            },
          ];
        }
      }

      const addSuggestions = slotOverride.mutable
        ? globalSuggestionsStore.getSuggestedElementsToAdd(slotOverride, i)
        : [];

      return [
        ...addSuggestions.map((suggestion) => ({
          item: suggestion,
          type: 'addSuggestion' as const,
        })),
        ...(item !== null
          ? [
              {
                item,
              },
            ]
          : []),
      ];
    },
  );

  items.forEach(({ type }, i) => {
    if (type !== 'addSuggestion') return;

    const marginOverrides = computeNewLocationMarginOverrides({
      slotItems: items.map(({ item }) => item),
      slotInfo,
      library,
      index: i,
    });
    for (const { index, marginOverride } of marginOverrides) {
      items[index].marginOverride = marginOverride;
    }
  });

  return items;
});

const getItemsWithDropPreview = computedFn(function (
  slotOverride: SlotOverride | SlotOverrideState,
  slotInfo: SlotInfo,
  library: LibraryCache,
  {
    source,
    dest,
    dropMode,
  }: { source: DragSource; dest: DropDest; dropMode: DropMode },
): ReadonlyArray<RenderableSlotItem> {
  if (!source.from) return [];

  const oldIndex = source.from.index;
  const newIndex = dest.index;

  const items: RenderableSlotItem[] = slotOverride.items.map((item) => ({
    item,
  }));

  const oldItem = slotOverride.items[oldIndex];

  const shouldApplyMarginOverrides = !(
    newIndex === oldIndex || newIndex === oldIndex + 1
  );

  if (shouldApplyMarginOverrides && dropMode !== 'duplicate') {
    const oldLocationMarginOverrides = computeOldLocationMarginOverrides({
      slotItems: slotOverride.items,
      slotInfo,
      index: oldIndex,
      library,
    });
    for (const { index, marginOverride } of oldLocationMarginOverrides) {
      items[index].marginOverride = marginOverride;
    }
  }

  items.splice(newIndex, 0, {
    item: source.el,
    type: 'dropPreview',
  });

  if (shouldApplyMarginOverrides || dropMode === 'duplicate') {
    const marginOverrides = computeNewLocationMarginOverrides({
      slotItems: items.map(({ item }) => item),
      slotInfo,
      index: newIndex,
      library,
    });
    for (const { index, marginOverride } of marginOverrides) {
      items[index].marginOverride = marginOverride;
    }
  }

  if (dropMode === 'duplicate') {
    return items;
  }

  // Remove the item being dragged from its original location.
  return items.filter(({ item }) => item !== oldItem);
});

export const getItemsForRendering = computedFn(function (
  slotOverride: SlotOverride | SlotOverrideState,
  slotInfo: SlotInfo,
  library: LibraryCache,
): ReadonlyArray<RenderableSlotItem> {
  const state = elementDndDomain.state.get();
  if (
    state?.dest?.slotOverride !== slotOverride ||
    state.source.from === undefined
  ) {
    return getItemsWithSuggestions(slotOverride, slotInfo, library);
  }

  return getItemsWithDropPreview(slotOverride, slotInfo, library, {
    source: state.source,
    dest: state.dest,
    dropMode: state.dropMode,
  });
});

export const SlotItemDraggable = observer(function _SlotItemDraggable({
  enable,
  interactable,
  children,
  sref,
  rootElement,
  library,
  renderDragPreview,
}: {
  enable: boolean;
  interactable: Interactable;
  children: React.ReactNode;
  sref: SectionRef;
  rootElement: ElementInstance;
  library: LibraryCache;
  renderDragPreview: () => React.ReactNode;
}) {
  if (!interactable.slotItem || !enable) {
    return <>{children}</>;
  }

  const slotType = library.getCachedSlotType(
    interactable.slotItem.slotInfo.typeId,
    { fetchIfMissing: true },
  )?.value;

  if (!slotType) {
    return <>{children}</>;
  }

  const { slotInfo, slotOverride, parentElement, itemElement } =
    interactable.slotItem;

  return (
    <elementDndDomain.Draggable
      source={
        canReorderInSlot(slotType)
          ? {
              el: itemElement.state,
              from: {
                slotInfo,
                slotOverride,
                slotType,
                mutableEl: itemElement,
                index: slotOverride.items.indexOf(itemElement),
                parentElement,
                rootElement,
              },
            }
          : undefined
      }
      supportsDuplicate={true}
      className={styles.draggable}
      dragPreviewClass={classNames(
        styles.dragPreview,
        sref.type === 'reference' && styles.global,
      )}
      renderDragPreview={renderDragPreview}
      noShadow
    >
      {children}
    </elementDndDomain.Draggable>
  );
});
